import { createSelector } from "reselect"
import { chain, find, get, isFinite, map } from "lodash"
import { safeDivide } from "helpers/math"

const EMPTY_OBJECT = {}
const EMPTY_ARRAY = []
export const getScannerChartPoints = state => get(state, "modules.compareFrontier.frontierChartData", EMPTY_ARRAY)
export const getForecast = state => get(state, "global.data.experimentForecastData.data.model.simulation.forecast.forecastData", EMPTY_OBJECT)
export const getForecasts = (state) => get(state, "global.data.forecasts.data.forecasts", EMPTY_ARRAY)
export const getPortfolioPositions = state => get(state, "global.data.experimentPortfolioData.data.portfolio.dates[0]")
export const getPortfolioData = state => get(state, "global.data.experimentPortfolioData.data.model.simulation")
const getCoverage = state => get(state, "global.data.experimentPortfolioData.data.model.portfolio.coverage.0.summary.percentGmvAvailable", 0)
export const getStatus = state => get(state, "modules.compareFrontier.status")
export const getReloadChart = state => get(state, "modules.compareFrontier.reloadChart")
export const getComparisonItemsStatus = state => get(state, "modules.compareFrontier.comparisonItemsStatus")

export const getPortfolioEquity = createSelector(
  [getPortfolioPositions, getCoverage],
  (positions, coverage) => {
    const portfolioEquity = get(positions, "equity")
    return isFinite(portfolioEquity) ? portfolioEquity : get(positions, "gmv") * coverage
  },
)

const getRawExpectedReturn = createSelector(
  [getForecast, getPortfolioPositions, getPortfolioEquity],
  (forecast, positions, portfolioEquity) => {
    const expectedEquitiesReturns = get(forecast, "equities")
    const equities = chain(positions)
      .get("equities")
      .sumBy(equity => {
        const size = safeDivide(equity.economicExposure, portfolioEquity)
        const equityReturn = find(expectedEquitiesReturns, erEquity =>
          erEquity.id.modelProviderId === equity.id.modelProviderId || (erEquity.id.ticker === equity.id.ticker && erEquity.id.mic === equity.id.mic),
        )

        if (equityReturn) {
          return size * equityReturn.expectedPercentReturn
        } else {
          return 0
        }
      })
      .value()

    const expectedSwapsReturns = get(forecast, "swaps")
    const swaps = chain(positions)
      .get("swaps")
      .sumBy(swap => {
        const size = safeDivide(swap.economicExposure, portfolioEquity)
        const swapReturn = find(expectedSwapsReturns, erSwap => erSwap.id === swap.id)

        if (swapReturn) {
          return size * swapReturn.expectedPercentReturn
        } else {
          return 0
        }
      })
      .value()

    return equities + swaps
  },
)

// Whatever the difference is between what's computed by the API (which is active)
// and what's computed locally (which is not) is the return of the benchmark
export const getBenchmarkReturn = createSelector(
  [getRawExpectedReturn, getForecast],
  (returnPerc, forecast) => {
    return returnPerc - get(forecast, "total", returnPerc)
  },
)

const adjustData = createSelector(
  [getScannerChartPoints, getBenchmarkReturn],
  (scannerPoints, benchmarkReturn) =>
    map(scannerPoints, ([predictedTotalRisk, expectedReturn]) => [predictedTotalRisk, expectedReturn - benchmarkReturn]),
)

export const getData = createSelector(
  [adjustData],
  (scannerChartPoints) => chain(scannerChartPoints)
    .take(25)
    .orderBy(point => point[0])
    .map(([predictedTotalRisk, expectedReturn]) => ({
      x: predictedTotalRisk,
      y: expectedReturn,
    }))
    .value(),
)
